// use-academy-tutor — EVE-Tutor-Chat für den Academy-Tab.
//
// Hält eine LOKALE Message-Liste (nicht im globalen eve-chat-store, weil der
// Tutor modul-scoped ist und beim Modulwechsel resettet wird). Jede Frage geht
// als `agentKey:'direct'` durch `runEveStreamed` — Billing läuft automatisch
// (Backend lädt das `chat`-Feature, 2 Credits). Der aktuelle Modul-Text wird
// über `ctx.academyModuleContext` mitgegeben → EVE antwortet im Modul-Kontext.

import { useCallback, useRef, useState } from 'react';
import {
  InsufficientCreditsError,
  runEveStreamed,
  type ChatHistoryTurn,
  type RuntimeContext,
} from '../services/eve-runtime';
import { selectAccessToken, useAuthStore } from '../stores/auth-store';

export interface TutorMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface Options {
  moduleContext: string;
  moduleTitle: string;
  onQuizResult?: (passed: boolean, score: number) => void;
}

export interface UseAcademyTutorReturn {
  messages: readonly TutorMessage[];
  loading: boolean;
  ask: (msg: string) => Promise<void>;
  startQuiz: () => Promise<void>;
  reset: () => void;
}

const QUIZ_PROMPT =
  'Starte ein kurzes Quiz: Stelle mir nacheinander 3 Verständnisfragen zu genau diesem Modul. ' +
  'Warte nach jeder Frage auf meine Antwort, bewerte sie knapp und gib am Ende „bestanden" oder ' +
  '„nicht bestanden" mit der Anzahl richtiger Antworten (z. B. „2/3 richtig").';

const ERROR_TEXT = '⚠ EVE konnte gerade nicht antworten. Versuch es gleich nochmal.';

/**
 * Heuristik: liest aus der finalen Quiz-Antwort von EVE Score + Pass/Fail.
 * Gibt `null` zurück, wenn KEIN auswertbares Muster gefunden wird (z. B. ein
 * abgebrochenes / unvollständiges Quiz) — dann wird nichts persistiert.
 */
export function parseQuizResult(text: string): { passed: boolean; score: number } | null {
  const scoreMatch = text.match(/(\d)\s*\/\s*3/);
  const hasBestanden = /bestanden/i.test(text);
  // Ohne „X/3" und ohne „bestanden" gilt das Quiz als nicht ausgewertet.
  if (!scoreMatch && !hasBestanden) return null;

  const notPassed = /nicht bestanden/i.test(text);

  let score: number;
  if (scoreMatch && scoreMatch[1]) {
    score = Number.parseInt(scoreMatch[1], 10);
  } else if (hasBestanden && !notPassed) {
    score = 3;
  } else {
    score = 0;
  }

  const passed = notPassed ? false : hasBestanden ? true : score >= 2;
  return { passed, score };
}

export function useAcademyTutor({
  moduleContext,
  moduleTitle,
  onQuizResult,
}: Options): UseAcademyTutorReturn {
  const accessToken = useAuthStore(selectAccessToken);
  const [messages, setMessages] = useState<readonly TutorMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const historyRef = useRef<ChatHistoryTurn[]>([]);

  const run = useCallback(
    async (userMsg: string, isQuiz = false): Promise<void> => {
      setLoading(true);
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: userMsg },
        { role: 'assistant', content: '' },
      ]);

      // History-Snapshot VOR der neuen Frage (immutable copy).
      const history = [...historyRef.current];
      historyRef.current = [...historyRef.current, { role: 'user', text: userMsg }];

      const ctx: RuntimeContext = {
        agentKey: 'direct',
        userMsg,
        marketStats: null,
        fullData: null,
        academyModuleContext: `Modul: ${moduleTitle}\n\n${moduleContext}`,
      };

      try {
        const full = await runEveStreamed(
          {
            agentKey: 'direct',
            knowledgeKey: 'academy',
            userMsg,
            ctx,
            history,
            onChunk: (delta) =>
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.role === 'assistant') {
                  next[next.length - 1] = { ...last, content: last.content + delta };
                }
                return next;
              }),
          },
          { accessToken },
        );
        historyRef.current = [...historyRef.current, { role: 'assistant', text: full }];
        if (isQuiz && onQuizResult) {
          const result = parseQuizResult(full);
          if (result) onQuizResult(result.passed, result.score);
        }
      } catch (e) {
        if (e instanceof InsufficientCreditsError) {
          // Modal wird vom runEveStreamed-Event übernommen. Die leere Assistant-
          // Platzhalter-Bubble entfernen UND den gescheiterten User-Turn aus der
          // History rollen — sonst bleibt ein Ghost-User-Turn ohne passende
          // Assistant-Antwort zurück und bricht beim nächsten Call die
          // Turn-Alternation.
          historyRef.current = historyRef.current.slice(0, -1);
          setMessages((prev) => prev.slice(0, -1));
        } else {
          console.error('[academy/tutor] failed', e);
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: 'assistant', content: ERROR_TEXT };
            return next;
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, moduleContext, moduleTitle, onQuizResult],
  );

  const ask = useCallback((msg: string) => run(msg), [run]);
  const startQuiz = useCallback(() => run(QUIZ_PROMPT, true), [run]);
  const reset = useCallback(() => {
    setMessages([]);
    historyRef.current = [];
  }, []);

  return { messages, loading, ask, startQuiz, reset };
}
